import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, AtSign, Gift, Tag, Sparkles } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service to continue');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name, formData.username);
      toast.success('Account created! Welcome to Slab Society!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-amber-500/20 border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center gap-2 text-center flex-wrap">
            <Gift className="w-5 h-5 text-amber-400 shrink-0" />
            <p className="text-sm text-white">
              <span className="font-semibold text-amber-400">Join Slab Society Gold</span>
              {' '}– Get your first month FREE with code{' '}
              <span className="font-mono font-bold bg-amber-500/30 px-2 py-0.5 rounded text-amber-300">SLAB100</span>
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src={process.env.REACT_APP_LOGO_URL} 
              alt="Slab Society"
              className="w-16 h-16 rounded-xl object-contain"
            />
          </Link>
          <h1 className="font-heading font-bold text-3xl uppercase text-white">Join The Society</h1>
          <p className="text-zinc-400 mt-2">Create your free account</p>
        </div>

        <form onSubmit={handleSubmit} className="stat-card p-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">Full Name</Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="input-field pl-12"
                required
                data-testid="register-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="username" className="text-zinc-300">Username</Label>
            <div className="relative">
              <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                placeholder="johndoe"
                className="input-field pl-12"
                required
                data-testid="register-username"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="input-field pl-12"
                required
                data-testid="register-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="input-field pl-12 pr-12"
                required
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="input-field pl-12"
                required
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          {/* Terms of Service Checkbox */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-zinc-900/50 border border-white/5">
            <Checkbox 
              id="terms" 
              checked={agreedToTerms}
              onCheckedChange={setAgreedToTerms}
              className="mt-0.5 border-zinc-600 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
              data-testid="register-terms-checkbox"
            />
            <Label htmlFor="terms" className="text-sm text-zinc-400 leading-relaxed cursor-pointer">
              I agree to the{' '}
              <Link 
                to="/terms" 
                className="text-blue-400 hover:text-blue-300 underline"
                target="_blank"
                data-testid="terms-link"
              >
                Terms of Service
              </Link>{' '}
              and understand that sellers pay $20/month while keeping 100% of sales, 
              and buyers pay a 3.5% transaction fee.
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-zinc-400 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
            Sign in
          </Link>
        </p>

        {/* Membership Benefits Link */}
        <div className="mt-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
          <p className="text-zinc-300 mb-2">
            Thinking about selling? Learn why sellers love us.
          </p>
          <Link 
            to="/membership" 
            className="text-emerald-400 hover:text-emerald-300 font-semibold inline-flex items-center gap-1"
            data-testid="membership-benefits-link"
          >
            See Membership Benefits →
          </Link>
        </div>

        {/* Promo Code Reminder */}
        <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-400">Don't forget your promo code!</p>
              <p className="text-xs text-zinc-400 mt-1">
                After signing up, subscribe to Gold membership and enter{' '}
                <span className="font-mono font-bold text-amber-300">SLAB100</span>{' '}
                at checkout. Look for the "Add promotion code" link during Stripe checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RegisterPage;
